import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BsSliders } from "react-icons/bs";
import { FaCheck } from "react-icons/fa";
import { BookingLocation } from "../../data/occupancy.api";

type Props = {
  data: BookingLocation[];
  onFiltered: (locations: BookingLocation[]) => void;
};

const BookingFilters = ({ data, onFiltered }: Props) => {
  const highlightColour = useColorModeValue("black", "lancsBlue.300");
  const highlightFg = useColorModeValue("white", "black");
  const tags = useMemo(() => {
    return Array.from(new Set([...data.map((loc) => loc.tags)].flat()));
  }, [data]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tagFilters, setTagFilters] = useState<string[]>([]);

  const filteredData = useMemo(() => {
    return data.filter((loc) => {
      return (
        tagFilters.length < 1 ||
        loc.tags.some((tag) => tagFilters.includes(tag))
      );
    });
  }, [data, tagFilters]);

  const clearFilters = useCallback(() => {
    setTagFilters([]);
  }, []);

  const enabledFilters = useMemo(() => {
    return tagFilters.length;
  }, [tagFilters]);

  useEffect(() => {
    onFiltered(filteredData);
  }, [filteredData, onFiltered]);

  return (
    <Box mb={4}>
      <Button
        position="relative"
        size="md"
        leftIcon={<BsSliders />}
        border={enabledFilters > 0 ? "2px solid" : undefined}
        borderColor={highlightColour}
        onClick={onOpen}
      >
        Show Advanced Filters
        {enabledFilters > 0 ? (
          <Center
            bg={highlightColour}
            color={highlightFg}
            rounded="full"
            w={5}
            h={5}
            position="absolute"
            top={-2}
            right={-2}
          >
            {enabledFilters}
          </Center>
        ) : null}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filters</ModalHeader>

          <ModalBody>
            <Stack>
              <Box>
                <Heading size="xs" mb={2}>
                  Tags
                </Heading>
                <Flex flexWrap="wrap" gap={1}>
                  {tags.map((t) => (
                    <Tag
                      as={Button}
                      leftIcon={tagFilters.includes(t) ? <FaCheck /> : <></>}
                      variant={tagFilters.includes(t) ? "solid" : "subtle"}
                      colorScheme={
                        tagFilters.includes(t) ? "lancsBlue" : "gray"
                      }
                      onClick={() => {
                        if (tagFilters.includes(t)) {
                          setTagFilters(tagFilters.filter((tag) => tag !== t));
                        } else {
                          setTagFilters([...tagFilters, t]);
                        }
                      }}
                    >
                      {t}
                    </Tag>
                  ))}
                </Flex>
              </Box>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Flex justify="space-between" w="full" gap={2}>
              <Button
                variant="link"
                opacity={0.7}
                onClick={() => {
                  clearFilters();
                  onClose();
                }}
              >
                Clear All
              </Button>
              <Button colorScheme="lancsBlue" onClick={onClose}>
                Show {filteredData.length} Locations
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BookingFilters;
